import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { Observable, Subscription, throwError } from "rxjs";
import { catchError, filter, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { Plant } from "src/model/class/general classes/plant";
import { User } from "src/model/class/general classes/user";
import { UserAuthenticationSettings } from "src/model/class/general classes/userAuthenticationSettings";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { UnthorizedAccessType } from "src/model/enum/enumLoginResponse";
import {
  ApplicationTheme,
  ToastConnectionCodes,
} from "src/components/shared-components/enum/enumMisc";
import { MomentKey } from "src/model/enum/enumMoment";
import { AuthError } from "src/model/interface/auth-error";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iCredential } from "src/model/interface/icredential";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iRefreshTokenData } from "src/model/interface/iRefreshTokeData";
import { LocalStorageService } from "src/service/local-storage.service";
import {
  currentUserSetAction,
  currentUserUnsetAction,
} from "src/state/actions/authentication.actions";
import { storageUnsetAllAuthKeyAction } from "src/state/actions/local-storage.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { AlertService } from "./alert.service";
import { BIGService } from "./big.service";
import { EventService } from "./event.service";
import { LoaderService } from "./loader.service";
import { LoggerService } from "./logger.service";
import { PlantService } from "./plant.service";
import { SecuretyDataProtectionService } from "./securety-data-protection.service";
import { SettingsService } from "./settings.service";
import { SystemRouterService } from "./system-router.service";
import { ThemeService } from "./theme.service";
import { ToastService } from "./toast.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})

/**
 *
 *  {@link AuthenticationService} manage all authentication operation: login/logout/refresh.
 *  Normally it store data into local storage (Browser: Indexedb).
 *
 *  BROWSER FALLBACK MANAGMENT: The service use a sessionStorage object to keep track current user navigation.
 *  When the user close the browser, it'll be destroyed and user it's logout. On new browser open, unless user check Autologin option,
 *  it'll perform new login
 *
 */
export class AuthenticationService implements OnDestroy, iEvent {
  private _logContext: string = "Authentication.service";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectCurrentUserSub: Subscription = null;
  //#endregion

  private _userStore: CurrentSessionData = null;
  private _translateSubscription: Subscription = null;

  private _lblSettingDownloadFailed: string = "";
  private _lblIpWanAccessLock: string = "";
  private _lblLoginFailedLicenceExpired: string = "";
  private _lblLoginFailed: string = "";
  private _lblLoginUnable: string = "";
  private _lblLoginFailedCredential: string = "";
  private _lblUserNotYetValid: string = "";
  private _lblUserAccessExpiredOn: string = "";
  private _lblTo: string = "";
  private _lblUserAccessWillBeActivateOn: string = "";

  //private _forceLogoutSubscription: Subscription = null;
  public _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _localStorageService: LocalStorageService,
    private _securetyDataProtectionService: SecuretyDataProtectionService,
    private _systemRouterService: SystemRouterService,
    private _settingService: SettingsService,
    private _eventService: EventService,
    private _alertService: AlertService,
    private _loaderController: LoaderService,
    private _toastService: ToastService,
    private _loggerService: LoggerService,
    private _store: Store,
    private _plantService: PlantService,
    private _bigService: BIGService,
    private _translateService: TranslateService,
    private _themeService: ThemeService
  ) {
    this.subscribeEvents();
    this.loadCurrentUser();
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
    this.unsubscribeEvents();
    this._translateSubscription?.unsubscribe();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeForceLogout",
          "Execute forced logout"
        );
        this.performLogout();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   *
   * {@link login} manage login API call, using a NOT-ENCRYPTED "iCredential" object.
   *
   *  @param userCredential : user credential object
   *
   */
  public login(userCredential: iCredential): Promise<any> {
    let pathLogin: string = "";
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "login()",
      "Executing LOGIN API call with credential: " +
      JSON.stringify(userCredential)
    );

    userCredential.IsSiboDevice = this._bigService.isMyToDevice;
    // userCredential.IsSiboDevice = this._bigService.isMyToDevice;
    /** Object encryption for secure login */
    const encryptedCredentials = this._securetyDataProtectionService.encrypt(
      JSON.stringify(userCredential)
    );

    pathLogin =
      environment.httpServerRoot + AppSettings.API_URL_AUTH_USER + "/login";

    return this._http
      .post(pathLogin, { credentials: encryptedCredentials })
      .pipe(
        tap((authResponse: UserAuthenticationSettings) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_http.post - /api/authentication/login ",
            "login()",
            "Response received"
          );

          const currentSession: CurrentSessionData =
            this.createUserSessionFromAuthResponse(
              authResponse,
              userCredential
            );

          this.setUserData(currentSession);

          // if (!this._themeService.isDigitalPlace) {
          //   this.assignUserDataToSelectedPlant(currentSession, userCredential);
          // }
        }),
        switchMap((authResponse: UserAuthenticationSettings) => {
          return this._settingService.getSettings();
        }),

        catchError((error: HttpErrorResponse) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.post -" + AppSettings.API_URL_AUTH_USER + "/login",
            "login()",
            "Error: " + error.message
          );

          if (typeof error.error === "object") {
            if (
              (error.error as AuthError).code === UnthorizedAccessType.WAN_BLOCK
            ) {
              this._loggerService.log(
                LogLevel.LOG_ERROR,
                this._logContext,
                "_http.post -" + AppSettings.API_URL_AUTH_USER + "/login",
                "login()",
                "Error code: " + (error.error as AuthError).code
              );
              this._eventService.OnPlantReceivedWanBlockResponse.next(true);
            }
          }

          return throwError(error);
        })
      )
      .toPromise();
  } //

  /**
   *   {@link getRefreshToken} get new token from existing token via refresh api token call
   *
   *   This function make only API call because all refresh token logic is made on AuthenticationInterceptor
   */
  public getRefreshToken(): Observable<any> {
    const refreshToken: iRefreshTokenData = {
      refreshToken: this._userStore.refreshToken,
      isSiboDevice: this._bigService.isMyToDevice,
    };

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_http.post - /api/authentication/refreshtoken ",
      "getRefreshToken()",
      "STARTING NEW REFRESH TOKEN with PREVIOUS TOKEN: " +
      refreshToken.refreshToken +
      " | isSiboDevice: " +
      refreshToken.isSiboDevice
    );

    return this._http
      .post<any>(
        environment.httpServerRoot +
        AppSettings.API_URL_AUTH_USER +
        "/refreshtoken",
        refreshToken
      )
      .pipe(
        map((refreshResponse: any) => {
          return this.createCurrentSessionObjectFromRefreshResponse(
            refreshResponse
          );
        }),
        tap((currentSession: CurrentSessionData) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_http.post - /api/authentication/refreshtoken ",
            "getRefreshToken()",
            "SETTING NEW REFRESH TOKEN TO STORE"
          );

          this.setUserData(currentSession);
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "_http.post - /api/authentication/refreshtoken ",
            "getRefreshToken()",
            "GOT NEW REFRESH TOKEN"
          );
        })
      );
  }

  /**
   * Perform logout operation.
   *
   * @param doLogoutApiCall Force logout API call
   * @returns
   */
  public async logout(doLogoutApiCall: boolean = true): Promise<void> {
    /** AB: after refresh token expiration, if you click on a supervision, loader is going to be loaded
     *    and stay locked on the screen; this command dismiss the loader (inside check if it exist) */
    await this._loaderController.dismissLoaderWaiting();

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_http.post - /api/authentication/logout ",
      "logout()",
      "Executing logout from: " + environment.httpServerRoot
    );

    try {
      if (doLogoutApiCall) {
        await this._http
          .post(
            environment.httpServerRoot +
            AppSettings.API_URL_AUTH_USER +
            "/logout",
            {}
          )
          .toPromise();

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_http.post - /api/authentication/logout ",
          "logout()",
          "API CALL TERMINATED"
        );
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "_http.post - /api/authentication/logout ",
        "logout()",
        "ERROR IN API CALL: " + error.message
      );
    } finally {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_http.post - /api/authentication/logout ",
        "logout()",
        "CONTINUE PROCEDURE"
      );
      await this.performLogout();
    }
  }

  /**
   *   {@link isAccessTokenValid} check if current accss token is valid by token expiration date
   */
  public isAccessTokenValid(currentSessionData: CurrentSessionData): boolean {
    const nowUTC = moment().utc();

    if (currentSessionData?.expireTokenAccessDate) {
      return nowUTC.isBefore(currentSessionData?.expireTokenAccessDate);
    }

    return false;
  }

  /**
   * {@link isRefreshTokenValid} check if current refresh token is valid by refresh token expiration date
   */
  public isRefreshTokenValid(currentSessionData: CurrentSessionData): boolean {
    const nowUTC = moment().utc();

    if (currentSessionData?.expireTokenRefreshDate) {
      return nowUTC.isBefore(currentSessionData?.expireTokenRefreshDate);
    }

    return false;
  }

  /**
   *  {@link performLogout} execute logout operation:
   *
   *  - Unset all storage key
   *  - Unset current session data
   *  - Propagate logout event to subscrie component/service/page
   *  - Redirect to homepage
   *  - Propagate the end of the logout (MQTT is disconnected here)
   */
  private async performLogout(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "performLogout()",
      "START"
    );

    // BROWSER FALLBACK: unset all storage key otherwise service are not injected
    this.unsetCurrentUserData();

    this._eventService.OnMakeLogout.next(null);

    await this.closeAllAlert();

    await this._toastService.closeToastConnectionIssues(
      ToastConnectionCodes.MQTT_CONNECTION_REGAIN
    );
    await this._toastService.closeToastConnectionIssues(
      ToastConnectionCodes.API_CONNECTION_REGAIN
    );

    /* The end of the logout is signalled once the pages of the reserved area are destroyed and have released
     their MQTT topics: closing the connection earlier made each late release fail with "client disconnecting". */
    const leavingPagesDestroyedSub: Subscription =
      this._systemRouterService.leavingPagesDestroyed$
        .pipe(take(1))
        .subscribe(() => this._eventService.OnLogoutCompleted.next());

    const hasLeftPage: boolean =
      await this._systemRouterService.navigateToHomepage();

    if (!hasLeftPage) {
      leavingPagesDestroyedSub.unsubscribe();
      this._eventService.OnLogoutCompleted.next();
    }

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "performLogout()",
      "END"
    );
  }

  /**
   * This method returns from server the complete list of App users which are:
   *  +  Not been deleted.
   *  +  Active; this means, their End-VAlidity date is equal OR greater than current SERVER'S time-date.
   * @returns
   */
  public async asyncgetActiveUsers(): Promise<Array<User>> {
    return this.getUsersByQueryString("isActive=TRUE");
  }

  private async getUsersByQueryString(
    queryString: string = ""
  ): Promise<Array<User>> {
    if (queryString.length > 0) {
      queryString = "?" + queryString;
    }

    return this._http
      .get<Array<User>>(
        environment.httpServerRoot + AppSettings.API_URL_USER + queryString
      )
      .toPromise();
  }

  /**
   * Close all open popup
   */
  private async closeAllAlert(): Promise<void> {
    await this._alertService.clearAlertOk();
    await this._alertService.clearAlertDisconnection();
    await this._alertService.clearAlertIpNotAllowed();
    await this._alertService.clearAlertCommandNotAvaiable();
  }

  /**
   *  {@link setUserData} save current user data into local storage and propagate it to all subscribe component/service/page
   *
   *  @param userLogged current user data
   */
  private setUserData(userLogged: CurrentSessionData): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_http.post - /api/authentication/refreshtoken ",
      "setUserData()",
      "new USer data: " + JSON.stringify(userLogged)
    );
    this._store.dispatch(currentUserSetAction({ currentUser: userLogged }));
  }

  /**
   * Unset current user data
   */
  public unsetCurrentUserData(): void {
    try {
      this._store.dispatch(currentUserUnsetAction());
      this._store.dispatch(storageUnsetAllAuthKeyAction());

      this._localStorageService.removeAllBIStudio2Key();
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "unsetCurrentUserData()",
        "EXCEPTION: " + (exception?.message ?? JSON.stringify(exception))
      );
    }
  }

  /**
   * Create user sessiont object from authentication response and user credentials
   *
   * @param authResponse Authentication response received from API
   * @param userCredential User credentials object
   * @returns
   */
  private createUserSessionFromAuthResponse(
    authResponse: UserAuthenticationSettings,
    userCredential: iCredential
  ): CurrentSessionData {
    const currentSession = new CurrentSessionData();
    currentSession.id = authResponse.id;
    currentSession.username = this._securetyDataProtectionService.encrypt(
      userCredential.Username
    );
    currentSession.password = this._securetyDataProtectionService.encrypt(
      userCredential.Password
    );

    currentSession.rememberCredentials = userCredential.RememberMe;
    currentSession.isAutoLoginOn = userCredential.isAutoLoginOn;

    /* NOTE: If the "AutoLogin" function has been selected, THEN ALSO the "Remenber Credentials" function is considered active*/
    if (currentSession.isAutoLoginOn) {
      currentSession.rememberCredentials = currentSession.isAutoLoginOn;
    }

    currentSession.accessToken = authResponse.accessToken;
    currentSession.refreshToken = authResponse.refreshToken;

    currentSession.role = authResponse.role;

    const expireTokenAccessDateUTC = moment()
      .utc()
      .add(authResponse.expirationAccessTokenTime, MomentKey.MINUTES);
    currentSession.expireTokenAccessDate = expireTokenAccessDateUTC.valueOf();
    currentSession.expireTokenAccessDateString =
      expireTokenAccessDateUTC.format(MomentFormats.MOMENT_DATETIME_FORMAT);

    const expireTokenRefreshDateUTC = moment()
      .utc()
      .add(authResponse.expirationRefreshTokenTime, MomentKey.MINUTES);
    currentSession.expireTokenRefreshDate = expireTokenRefreshDateUTC.valueOf();
    currentSession.expireTokenRefreshDateString =
      expireTokenRefreshDateUTC.format(MomentFormats.MOMENT_DATETIME_FORMAT);

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_http.post - /api/authentication/login ",
      "createUserSessionFromAuthResponse()",
      "EXPIRATION ACCESS TOKEN DATE (as object Moment) " +
      expireTokenAccessDateUTC
    );
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_http.post - /api/authentication/login ",
      "createUserSessionFromAuthResponse()",
      "EXPIRATION ACCESS TOKEN DATE (as setted in local storage) " +
      currentSession.expireTokenAccessDate
    );
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "_http.post - /api/authentication/login ",
      "createUserSessionFromAuthResponse()",
      "EXPIRATION REFRESH TOKEN TIME from server: " +
      authResponse.expirationRefreshTokenTime
    );

    return currentSession;
  }

  /**
   * Create current session user from refresh response
   *
   * @param refreshResponse Refresh response object
   * @returns
   */
  private createCurrentSessionObjectFromRefreshResponse(
    refreshResponse: UserAuthenticationSettings
  ): CurrentSessionData {
    const currentSession: CurrentSessionData = new CurrentSessionData();

    try {
      currentSession.id = refreshResponse.id;
      currentSession.username = this._userStore.username;
      currentSession.password = this._userStore.password;

      currentSession.rememberCredentials = this._userStore.rememberCredentials;
      currentSession.isAutoLoginOn = this._userStore.isAutoLoginOn;

      currentSession.accessToken = refreshResponse?.accessToken;
      currentSession.refreshToken = refreshResponse?.refreshToken;

      currentSession.role = refreshResponse?.role;
      currentSession.name = refreshResponse?.name;
      currentSession.surname = refreshResponse?.surname;
      currentSession.commercialComunication =
        refreshResponse?.commercialComunication;
      currentSession.personalizingService =
        refreshResponse?.personalizingService;
      currentSession.idUserRemote = refreshResponse?.idUserRemote;

      const expireTokenAccessDateUTC = moment()
        .utc()
        .add(refreshResponse.expirationAccessTokenTime, MomentKey.MINUTES);
      //currentSession.expireTokenAccessDate = expireTokenAccessDateUTC;
      currentSession.expireTokenAccessDate = expireTokenAccessDateUTC.valueOf();
      currentSession.expireTokenAccessDateString =
        expireTokenAccessDateUTC.format(MomentFormats.MOMENT_DATETIME_FORMAT);

      const expireTokenRefreshDateUTC = moment()
        .utc()
        .add(refreshResponse.expirationRefreshTokenTime, MomentKey.MINUTES);
      //currentSession.expireTokenRefreshDate = expireTokenRefreshDateUTC;
      currentSession.expireTokenRefreshDate =
        expireTokenRefreshDateUTC.valueOf();
      currentSession.expireTokenRefreshDateString =
        expireTokenRefreshDateUTC.format(
          MomentFormats.MOMENT_DATETIME_FORMAT
        );
    } catch (exception) { }

    return currentSession;
  }

  /**
   * Assign user data like username, password, autologin active, and remember me to selected plant
   * for next login
   *
   * @param currentSessionuser Current session generated
   * @param userCredential User credentials
   */
  private assignUserDataToSelectedPlant(
    currentSessionuser: CurrentSessionData,
    userCredential: iCredential
  ): void {
    this._store
      .select(selectedPlant)
      .pipe(take(1))
      .subscribe((currentPlant: Plant) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_http.post - /api/authentication/login ",
          "login()",
          "POst-LOGIN update plant. REMEMBER CREDENTIAL: " +
          currentSessionuser.rememberCredentials +
          "; AUTOLOGIN: " +
          currentSessionuser.isAutoLoginOn
        );

        const username: string = currentSessionuser.rememberCredentials
          ? userCredential.Username
          : "";
        const password: string = currentSessionuser.rememberCredentials
          ? userCredential.Password
          : "";
        const rememberMe: boolean = currentSessionuser.rememberCredentials;
        const isAutologinActive: boolean = currentSessionuser.isAutoLoginOn;

        const updatePlant: Plant = new Plant({
          ...currentPlant,
          Username: username,
          Password: password,
          RememberMe: rememberMe,
          isAutoLoginOn: isAutologinActive,
        });

        this._plantService.updatePlant(updatePlant);
      });
  }

  /**
   * Load current user from fstore
   */
  private loadCurrentUser(): void {
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => currentUserData.currentUser)
      )
      .subscribe((currentUser: CurrentSessionData) => {
        this._userStore = currentUser;
      });
  }

  /**
   * Check if user is authenticated
   */
  public isUserAuthenticated(): boolean {
    return !!this._userStore;
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectCurrentUserSub) {
      this._selectCurrentUserSub.unsubscribe();
    }
  }
  //#endregion

  //#region Alert login/authentication-related

  /**
   * Handle un authorized errorr response
   *
   * @param error
   */
  public async handleUnthorizedLogin(error: HttpErrorResponse): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      this._logContext,
      "handleUnthorizedLogin()",
      "ERROR STATUS: " + error.status + " | msg: " + error.message
    );

    let stringTextAlert = this._lblSettingDownloadFailed;
    //close loader popup

    await this._loaderController.dismissLoaderWaiting();

    if (error?.status === 401) {
      stringTextAlert = this.get401ErrorResponseText(error?.error);

      //this._errorCredentialLoginCount++;
    } else if (error?.status === 503) {
      stringTextAlert = this._lblLoginFailedLicenceExpired;
    } else {
      stringTextAlert = this._lblLoginFailed;
    }

    //present error popup
    await this._alertService.clearAlertOk();
    await this._alertService.presentAlertOk(
      this._lblLoginUnable,
      "",
      stringTextAlert
    );
  }

  /**
   * Return text for 401 error response
   *
   * @param authErrorObject Authentication error object
   * @returns
   */
  private get401ErrorResponseText(authErrorObject: AuthError): string {
    let stringTextAlert = this._lblLoginFailedCredential;

    if (authErrorObject) {
      switch (authErrorObject.code) {
        case UnthorizedAccessType.WAN_BLOCK:
          stringTextAlert = this._lblIpWanAccessLock;
          break;
        case UnthorizedAccessType.USER_EXPIRED:
          stringTextAlert = this.getStringTextFromUserExpiredError(
            authErrorObject.extraInfo["EndValidity"]
          );
          break;
        case UnthorizedAccessType.USER_NOT_YET_VALID:
          stringTextAlert = this.getStringTextFromUserNotYetValidError(
            authErrorObject.extraInfo["StartValidity"]
          );

          break;
      }
    }

    return stringTextAlert;
  }

  /**
   * Return formatted string text where user is expired
   *
   * @param userEndValidity User expired date
   * @returns Formatted  user expired text
   */
  private getStringTextFromUserExpiredError(userEndValidity: string): string {
    const date: string = moment(
      userEndValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_DATE_EXTENDED);
    const time: string = moment(
      userEndValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTES);
    const year: number = moment(
      userEndValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).year();

    // Workaround to present generic message when user is not still
    // active and end validity year is before 2020
    if (year <= 2020) {
      return this._lblUserNotYetValid;
    }

    return (
      this._lblUserAccessExpiredOn + " " + date + " " + this._lblTo + " " + time
    );
  }

  /**
   * Return formatted string text where user is not yet valid
   *
   * @param userStartValidity User start validity
   * @returns Formatted user not yet valid text
   */
  private getStringTextFromUserNotYetValidError(
    userStartValidity: string
  ): string {
    const date: string = moment(
      userStartValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_DATE_EXTENDED);
    const time: string = moment(
      userStartValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTES);
    const year: number = moment(
      userStartValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).year();

    // Workaround to present generic message when user is not still
    // active and start validity year is before 2020
    if (year <= 2020) {
      return this._lblUserNotYetValid;
    }

    return (
      this._lblUserAccessWillBeActivateOn +
      " " +
      date +
      " " +
      this._lblTo +
      " " +
      time
    );
  }

  //#endregion

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.settingDownloadFailed",
        "dictionary.ipNotAllowedForConnectionMessage",
        "dictionary.loginExpiredLicence",
        "dictionary.loginFailed",
        "dictionary.loginUnable",
        "dictionary.loginWrongCredentials",
        "dictionary.userNotYetValid",
        "dictionary.userAccessExpiredOn",
        "dictionary.to",
        "dictionary.userAccessWillBeActivateOn",
      ])
      .subscribe((translation) => {
        this._lblSettingDownloadFailed =
          translation["dictionary.settingDownloadFailed"];
        this._lblIpWanAccessLock =
          translation["dictionary.ipNotAllowedForConnectionMessage"];
        this._lblLoginFailedLicenceExpired =
          translation["dictionary.loginExpiredLicence"];
        this._lblLoginFailed = translation["dictionary.loginFailed"];
        this._lblLoginUnable = translation["dictionary.loginUnable"];
        this._lblLoginFailedCredential =
          translation["dictionary.loginWrongCredentials"];
        this._lblUserNotYetValid = translation["dictionary.userNotYetValid"];
        this._lblUserAccessExpiredOn =
          translation["dictionary.userAccessExpiredOn"];
        this._lblTo = translation["dictionary.to"];
        this._lblUserAccessWillBeActivateOn =
          translation["dictionary.userAccessWillBeActivateOn"];
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion
}
